/**
 * Format a date string for display without timezone shifting.
 * Handles date-only strings (YYYY-MM-DD) and full ISO timestamps.
 */
export const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }
  // For date-only strings (YYYY-MM-DD), parse as local time to avoid timezone shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  }
  // For ISO timestamps, parse normally
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

/**
 * Format a number as USD currency with proper formatting ($XX,XXX.XX)
 */
export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '-';
  }
  return Number(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format a file size in bytes to human-readable format (KB, MB, GB)
 */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined) {
    return '-';
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(1)} KB`;
  }
  const mb = kb / 1024;
  if (mb < 1024) {
    return `${mb.toFixed(1)} MB`;
  }
  return `${(mb / 1024).toFixed(1)} GB`;
};

/**
 * Custom filter function for amount columns in PrimeReact DataTable.
 * Parses the filter value as a number and compares against the absolute amount.
 * Handles currency formatting (commas, dollar signs) in the filter input.
 */
export const amountFilterFunction = (
  value: number | string | null | undefined,
  filter: string | null | undefined
): boolean => {
  console.log('amountFilterFunction called:', { value, filter, valueType: typeof value });

  if (filter === null || filter === undefined || filter === '') {
    return true;
  }
  if (value === null || value === undefined) {
    return false;
  }

  // Convert value to number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return false;
  }

  // Strip currency formatting from filter input ($, commas)
  const cleanFilter = filter.replace(/[$,]/g, '').trim();
  const filterNum = parseFloat(cleanFilter);
  if (isNaN(filterNum)) {
    return true; // Invalid filter input, show all
  }

  // Compare absolute values with small tolerance for floating point
  const absValue = Math.abs(numValue);
  const result = Math.abs(absValue - filterNum) < 0.01;
  console.log('amountFilterFunction result:', { absValue, filterNum, result });
  return result;
};
